import { queryOptions } from '@tanstack/react-query';
import ApiHttpClientService from '@frodo/lib/services/http/base/api-http-client.service';
import { BFF_API_URL } from '@frodo/lib/services/http/shared/constants';
import { QUERY_KEYS } from '@frodo/lib/services/http/shared/queryKeys';
import { Product, ProductQueryOptions } from '@frodo/models/types';

class ApiProductClientService extends ApiHttpClientService {
  async fetchProducts(
    sku: string | string[],
    { isDetailed, isVideoEnabled, imageSize }: ProductQueryOptions = {},
  ): Promise<Product[]> {
    const response = await this.get<Product[] | null>(BFF_API_URL.productData, {
      params: {
        sku: Array.isArray(sku) ? sku.join(',') : sku,
        isDetailed,
        isVideoEnabled,
        imageSize,
      },
    });

    return response ?? [];
  }
}

export const apiProductClientService = new ApiProductClientService();

const createProductQuery = <TData = Product[]>(
  queryKey: readonly unknown[],
  sku: string | string[],
  options: ProductQueryOptions = {},
  transform: (products: Product[]) => TData = products => products as TData,
) =>
  queryOptions({
    queryKey,
    queryFn: () =>
      apiProductClientService.fetchProducts(sku, options).then(transform),
    throwOnError: false,
    retry: false,
  });

export const productSingleClientQuery = (
  sku: string,
  { isDetailed, isVideoEnabled, imageSize }: ProductQueryOptions = {},
) =>
  createProductQuery(
    QUERY_KEYS.singleProduct(sku, { isDetailed, isVideoEnabled, imageSize }),
    sku,
    { isDetailed, isVideoEnabled, imageSize },
    products => products[0] ?? null,
  );

export const productListClientQuery = (
  skuList: string[],
  { isDetailed, isVideoEnabled, imageSize }: ProductQueryOptions = {},
) =>
  createProductQuery(
    QUERY_KEYS.productList(skuList, { isDetailed, isVideoEnabled, imageSize }),
    skuList,
    { isDetailed, isVideoEnabled, imageSize },
  );
